/**
 * ADR-0121 Slice 2 — policy export shape + download helpers.
 *
 * The active policy and the current draft project to a single shareable
 * JSON shape:
 *
 *   {
 *     "_meta": { "exported_at": ISO, "exported_from_site_id": SiteId, "source": "active" | "draft" },
 *     "grammar_version": 1,
 *     "version": "v42",          // present for `active`; OMITTED for `draft`
 *     "rules": [ /\* canonical rule shape with deterministic key order *\/ ]
 *   }
 *
 * Strips on every export: account_id, policy_id, created_by, created_at,
 * active (server-side metadata that's wrong-by-construction to import
 * elsewhere). Pure functions + a single DOM helper at the bottom.
 */
import type { Rule, SiteId } from '@sill/types';

export interface ExportEnvelope {
  _meta: {
    exported_at: string;
    exported_from_site_id: SiteId;
    source: 'active' | 'draft';
  };
  grammar_version: number;
  /** Present for `active`; OMITTED for `draft`. */
  version?: string;
  rules: ExportedRule[];
}

/**
 * Server-side metadata (account_id, policy_id, created_by, created_at,
 * active) is wrong-by-construction to ship in a portable file, and the
 * `Rule` type from `@sill/types` does not carry it — so the export type
 * is just an ordered projection of `Rule`.
 */
export interface ExportedRule {
  rule_id: string;
  type: string;
  category: string;
  params: Record<string, unknown>;
  action_on_match: string;
  order: number;
  enabled: boolean;
  display_name?: string;
  description?: string;
}

/**
 * Deterministic per-rule key order for a git-friendly diff. JSON.stringify
 * follows insertion order on V8 / modern JS engines, so building the rule
 * object in the order below produces a stable shape.
 *
 * `params` keys are sorted alphabetically per-rule-type. Currency-keyed
 * objects (`caps`, `auto_approve_caps`, `floors_minor`) are sorted by
 * ISO-4217 code. Merchant-authored arrays (countries, manifest_hashes,
 * allowlisted_agent_ids, allowed_delegators) PRESERVE input order — they
 * are merchant intent, not internal data.
 */
export function canonicalizeRuleForExport(rule: Rule): ExportedRule {
  const out: ExportedRule = {
    rule_id: rule.rule_id,
    type: rule.type,
    category: rule.category,
    params: canonicalizeParams(rule.params as Record<string, unknown>),
    action_on_match: rule.action_on_match,
    order: rule.order,
    enabled: rule.enabled,
  };
  if (typeof rule.display_name === 'string') out.display_name = rule.display_name;
  if (typeof rule.description === 'string') out.description = rule.description;
  return out;
}

/**
 * Sort `params` keys alphabetically. Currency-keyed objects (`caps`,
 * `auto_approve_caps`, `floors_minor`) are then sorted by currency code.
 * Arrays of merchant intent stay in input order.
 */
function canonicalizeParams(params: Record<string, unknown>): Record<string, unknown> {
  const sortedKeys = Object.keys(params).sort();
  const out: Record<string, unknown> = {};
  for (const k of sortedKeys) {
    const v = params[k];
    if (
      (k === 'caps' || k === 'auto_approve_caps' || k === 'floors_minor')
      && v !== null
      && typeof v === 'object'
      && !Array.isArray(v)
    ) {
      out[k] = sortByCurrencyCode(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function sortByCurrencyCode(obj: Record<string, unknown>): Record<string, unknown> {
  const sortedKeys = Object.keys(obj).sort();
  const out: Record<string, unknown> = {};
  for (const k of sortedKeys) out[k] = obj[k];
  return out;
}

export interface BuildExportEnvelopeInput {
  source: 'active' | 'draft';
  site_id: SiteId;
  rules: readonly Rule[];
  /** Present for `active`; OMITTED for `draft`. */
  version?: string;
  grammar_version?: number;
  /** Injectable clock for deterministic tests. */
  now?: () => Date;
}

/**
 * Build the export envelope. Top-level key order: `_meta`, `grammar_version`,
 * `version` (if present), `rules` (matches §2.5).
 *
 * SECURITY (§3 must-hold 4): never include `account_id`, `policy_id`,
 * `created_by`, `created_at`, or `active`. The `Rule` type already lacks
 * those fields by schema, but `canonicalizeRuleForExport` is the chokepoint
 * that ensures only the projected fields ship.
 */
export function buildExportEnvelope(input: BuildExportEnvelopeInput): ExportEnvelope {
  const now = (input.now ?? (() => new Date()))();
  const envelope: ExportEnvelope = {
    _meta: {
      exported_at: now.toISOString(),
      exported_from_site_id: input.site_id,
      source: input.source,
    },
    grammar_version: input.grammar_version ?? 1,
    ...(input.source === 'active' && input.version !== undefined
      ? { version: input.version }
      : {}),
    rules: input.rules.map(canonicalizeRuleForExport),
  };
  return envelope;
}

/**
 * Pretty-print + a trailing newline. Pretty-printed for git-friendly
 * diffs; trailing newline so POSIX text-file conventions hold.
 */
export function serializeExportEnvelope(env: ExportEnvelope): string {
  return JSON.stringify(env, null, 2) + '\n';
}

/**
 * Filename — `policy-{site_id}-{version|draft}-{YYYY-MM-DD}.json`.
 */
export function buildExportFilename(input: {
  source: 'active' | 'draft';
  site_id: SiteId;
  version?: string;
  now?: () => Date;
}): string {
  const date = (input.now ?? (() => new Date()))();
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const label = input.source === 'draft' ? 'draft' : (input.version ?? 'active');
  // Strip any characters that would be unsafe in a filename — the version
  // string can carry a 'v' prefix and dashes/dots, but never something
  // odd. Keep [A-Za-z0-9._-]+.
  const safeLabel = label.replace(/[^A-Za-z0-9._-]+/g, '_');
  return `policy-${input.site_id}-${safeLabel}-${yyyy}-${mm}-${dd}.json`;
}

/**
 * Trigger a browser download of `contents` as `filename` using the
 * standard hidden <a download> + URL.createObjectURL pattern. The blob
 * URL is revoked in the next microtask so the page does not accumulate
 * blob refs.
 *
 * No-op (silently) when called outside the browser (SSR / test env
 * without jsdom DOM) — callers gate on `typeof window !== 'undefined'`
 * before invoking, but this is defense-in-depth.
 */
export function downloadFile(filename: string, contents: string, mime = 'application/json'): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
